import {
	BrandAssets,
	BrandColors,
	BrandHero,
	BrandLogos,
	BrandOverview,
	BrandSections,
	BrandStory,
	BrandTypography,
	BrandVoiceSection,
	paletteWithColorAdded,
	paletteWithColorRemoved,
	paletteHexes,
	primaryHex,
	toAssets,
	toFontFiles,
	toLogoVariants,
	toSwatches,
	toTypefaces,
	type BrandAsset,
	type BrandLayoutSection,
	type BrandLogoContext,
	type BrandLogoVariant,
	type BrandSwatch,
} from '@codezo/brand-detail';
import {
	toBrandColorPaletteInput,
	useDeleteBrandFileMutation,
	useGetBrandQuery,
	useUpdateBrandMutation,
} from '@codezo/graphql';
import { ArrowLeft, Download, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { FilePreview, type FilePreviewItem } from '../../components/ui/file-preview';
import { formatDate } from '../../lib/utils';
import { AddColorModal, type ColorEntry } from './AddColorModal';
import { EditBrandStoryModal } from './EditBrandStoryModal';
import { EditBrandVoiceModal, type BrandVoiceDraft } from './EditBrandVoiceModal';
import { UploadBrandAssetModal } from './UploadBrandAssetModal';

/**
 * Brand detail, v2 — the guideline hub.
 *
 * The default at `/brands/:id`, the same way order and quote v2 took over their
 * routes; the page it replaced is still at `/brands/:id/v1`.
 *
 * The page itself is `@codezo/brand-detail`; what lives here
 * is everything that is this app's: the query, the mutations, the modals, and
 * who is allowed to press what.
 *
 * An org user is looking at their OWN brand, so every edit affordance is on.
 * The admin copy of this page differs in exactly that respect, which is why the
 * package takes actions as slots rather than booleans.
 */
export function BrandDetailV2Page() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const [uploadOpen, setUploadOpen] = useState(false);
	const [addColorOpen, setAddColorOpen] = useState(false);
	const [storyOpen, setStoryOpen] = useState(false);
	const [voiceOpen, setVoiceOpen] = useState(false);
	const [logoContext, setLogoContext] = useState<BrandLogoContext>('auto');
	const [assetCategory, setAssetCategory] = useState('all');
	const [deleteFile, setDeleteFile] = useState<{ id: string; name: string } | null>(null);
	const [deleteColor, setDeleteColor] = useState<BrandSwatch | null>(null);
	const [preview, setPreview] = useState<{ files: FilePreviewItem[]; index: number } | null>(
		null
	);

	/* Filled by `BrandSections` with "take me to this section" — the overview's
	   stat cells are outside the sections and point into them. */
	const revealRef = useRef<((sectionId: string) => void) | null>(null);

	const { data, loading, refetch } = useGetBrandQuery({
		variables: { id: id! },
		fetchPolicy: 'no-cache',
		skip: !id,
	});

	/* Thumbnails are rendered on a queue after the upload is confirmed, so the
	 * immediate refetch usually lands before one exists. One delayed retry fills
	 * the tile in; polling would be pointless, since a file whose format isn't
	 * renderable never gets a thumbnail at all. (Carried over from v1.) */
	const refetchTimer = useRef<number | null>(null);
	useEffect(
		() => () => {
			if (refetchTimer.current !== null) window.clearTimeout(refetchTimer.current);
		},
		[]
	);

	const handleAssetUploaded = () => {
		void refetch();
		if (refetchTimer.current !== null) window.clearTimeout(refetchTimer.current);
		refetchTimer.current = window.setTimeout(() => {
			refetch().catch(() => {});
		}, 4000);
	};

	const [updateBrand, { loading: savingBrand }] = useUpdateBrandMutation();
	const [deleteBrandFile, { loading: deletingFile }] = useDeleteBrandFileMutation();

	const brand = data?.getBrand;

	const handleAddColor = async (color: ColorEntry) => {
		if (!brand || !id) return;
		try {
			await updateBrand({
				variables: {
					input: {
						id,
						colorPalette: toBrandColorPaletteInput(
							paletteWithColorAdded(brand, color.category, color)
						),
					},
				},
			});
			toast.success('Color added');
			setAddColorOpen(false);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to add color');
		}
	};

	const handleDeleteColor = async () => {
		if (!brand || !id || !deleteColor) return;
		try {
			await updateBrand({
				variables: {
					input: {
						id,
						colorPalette: toBrandColorPaletteInput(
							paletteWithColorRemoved(brand, deleteColor.category, deleteColor.index)
						),
					},
				},
			});
			toast.success('Color removed');
			setDeleteColor(null);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove color');
		}
	};

	const handleDeleteFile = async () => {
		if (!deleteFile || !id) return;
		try {
			await deleteBrandFile({ variables: { brandId: id, fileId: deleteFile.id } });
			toast.success('File deleted');
			setDeleteFile(null);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete file');
		}
	};

	/* Story and voice are two fields of one record, so they share a saver.
	   `brandPersonality` is replaced wholesale by the backend, which is why the
	   voice save resends `aiGuidance` — a field nothing on this page shows, and
	   that omitting would delete. */
	const handleSaveStory = async (description: string) => {
		if (!id) return;
		try {
			await updateBrand({ variables: { input: { id, description } } });
			toast.success('Brand story updated');
			setStoryOpen(false);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save brand story');
		}
	};

	const handleSaveVoice = async (draft: BrandVoiceDraft) => {
		if (!id) return;
		try {
			await updateBrand({
				variables: {
					input: {
						id,
						brandPersonality: {
							tone: draft.tone,
							traits: draft.traits,
							values: draft.values,
							aiGuidance: brand?.brandPersonality?.aiGuidance,
						},
					},
				},
			});
			toast.success('Tone of voice updated');
			setVoiceOpen(false);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save tone of voice');
		}
	};

	const view = useMemo(() => (brand ? buildView(brand) : null), [brand]);

	/* Opening a file opens its whole section in the previewer, so a logo pack is
	   arrowed through rather than clicked one at a time. */
	const openPreview = (items: FilePreviewItem[], fileId: string) => {
		const index = items.findIndex((f) => f.id === fileId);
		if (index >= 0) setPreview({ files: items, index });
	};

	if (loading || !brand || !view) {
		return (
			<Frame onBack={() => navigate('/profile#brands')}>
				<div className="h-[9.5rem] animate-pulse rounded-[var(--r-4)] bg-surface-fill" />
				<div className="mt-8 grid gap-8 lg:grid-cols-[12rem_minmax(0,1fr)]">
					<div className="hidden h-64 animate-pulse rounded-[var(--r-2)] bg-surface-fill lg:block" />
					<div className="flex flex-col gap-11">
						{Array.from({ length: 3 }).map((_, i) => (
							<div
								key={i}
								className="h-44 animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface-fill/50"
							/>
						))}
					</div>
				</div>
			</Frame>
		);
	}

	const sections: BrandLayoutSection[] = [
		{
			id: 'overview',
			label: 'Overview',
			content: (
				<BrandOverview
					description={brand.description}
					stats={view.stats}
					onStatClick={(sectionId) => revealRef.current?.(sectionId)}
				/>
			),
		},
		{
			id: 'story',
			label: 'Who we are',
			content: (
				<BrandStory
					description={brand.description}
					facts={view.facts}
					traits={brand.brandPersonality?.traits}
					action={<EditAction label="Edit story" onClick={() => setStoryOpen(true)} />}
				/>
			),
		},
		{
			id: 'voice',
			label: 'Tone of voice',
			content: (
				<BrandVoiceSection
					voice={brand.brandPersonality ?? {}}
					action={<EditAction label="Edit voice" onClick={() => setVoiceOpen(true)} />}
				/>
			),
		},
		{
			id: 'logo',
			label: 'Logo',
			count: view.logos.length,
			content: (
				<BrandLogos
					variants={view.logos}
					context={logoContext}
					onContextChange={setLogoContext}
					brandHex={view.primary}
					onOpen={(fileId) => openPreview(view.logoPreviews, fileId)}
					onAdd={() => setUploadOpen(true)}
					renderActions={(variant) => (
						<TileDelete
							onClick={() => setDeleteFile({ id: variant.id, name: variant.name })}
						/>
					)}
					action={
						<Button variant="ghost" size="sm" onClick={() => setUploadOpen(true)}>
							<Upload size={14} data-icon="inline-start" />
							Upload
						</Button>
					}
				/>
			),
		},
		{
			id: 'colors',
			label: 'Colors',
			count: view.swatches.length,
			content: (
				<BrandColors
					swatches={view.swatches}
					onAdd={() => setAddColorOpen(true)}
					renderActions={(swatch) => (
						<TileDelete onClick={() => setDeleteColor(swatch)} />
					)}
					action={
						<Button variant="ghost" size="sm" onClick={() => setAddColorOpen(true)}>
							<Plus size={14} data-icon="inline-start" />
							Add color
						</Button>
					}
				/>
			),
		},
		{
			id: 'typography',
			label: 'Typography',
			count: view.typefaces.length,
			content: (
				<BrandTypography
					typefaces={view.typefaces}
					fontFiles={view.fontFiles}
					guidelines={brand.typography?.guidelines}
				/>
			),
		},
		{
			id: 'assets',
			label: 'Assets & downloads',
			count: view.assets.length,
			content: (
				<BrandAssets
					assets={view.assets}
					category={assetCategory}
					onCategoryChange={setAssetCategory}
					onOpen={(fileId) => openPreview(view.assetPreviews, fileId)}
					renderActions={(asset) => (
						<>
							{asset.url && (
								<a
									href={asset.url}
									download={asset.name}
									title="Download"
									className="flex h-6 w-6 items-center justify-center rounded-[var(--r-2)] text-text-4 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<Download size={13} />
								</a>
							)}
							<button
								type="button"
								title="Delete"
								onClick={() => setDeleteFile({ id: asset.id, name: asset.name })}
								className="flex h-6 w-6 items-center justify-center rounded-[var(--r-2)] text-text-4 transition-colors hover:bg-surface-hover hover:text-text-1"
							>
								<Trash2 size={13} />
							</button>
						</>
					)}
					action={
						/* Ghost + Plus, like every other section head on this page.
						   It was the one `outline` button among them, which read
						   as a different class of control rather than the same
						   "add one of these" the others offer. */
						<Button variant="ghost" size="sm" onClick={() => setUploadOpen(true)}>
							<Plus size={14} data-icon="inline-start" />
							Add asset
						</Button>
					}
				/>
			),
		},
	];

	return (
		<Frame onBack={() => navigate('/profile#brands')}>
			{preview && (
				<FilePreview
					files={preview.files}
					initialIndex={preview.index}
					onClose={() => setPreview(null)}
				/>
			)}

			<UploadBrandAssetModal
				open={uploadOpen}
				onOpenChange={setUploadOpen}
				brandId={id!}
				onSuccess={handleAssetUploaded}
			/>
			<EditBrandStoryModal
				open={storyOpen}
				onOpenChange={setStoryOpen}
				description={brand.description}
				onSave={handleSaveStory}
				loading={savingBrand}
			/>
			<EditBrandVoiceModal
				open={voiceOpen}
				onOpenChange={setVoiceOpen}
				voice={brand.brandPersonality ?? {}}
				onSave={handleSaveVoice}
				loading={savingBrand}
			/>
			<AddColorModal
				open={addColorOpen}
				onOpenChange={setAddColorOpen}
				onSave={handleAddColor}
				loading={savingBrand}
			/>
			<ConfirmDialog
				open={!!deleteFile}
				onOpenChange={(v) => !v && setDeleteFile(null)}
				title="Delete file"
				description={`Are you sure you want to delete "${deleteFile?.name}"?`}
				confirmLabel="Delete"
				loading={deletingFile}
				onConfirm={handleDeleteFile}
			/>
			<ConfirmDialog
				open={!!deleteColor}
				onOpenChange={(v) => !v && setDeleteColor(null)}
				title="Delete color"
				description={`Are you sure you want to remove "${deleteColor?.name}" from the palette?`}
				confirmLabel="Delete"
				loading={savingBrand}
				onConfirm={handleDeleteColor}
			/>

			<BrandHero
				name={brand.name}
				logoUrl={brand.logo?.thumbnailUrl ?? brand.logo?.url}
				colors={view.colors}
				meta={
					brand.updatedAt
						? `Updated ${formatDate(brand.updatedAt)}`
						: brand.createdAt
							? `Created ${formatDate(brand.createdAt)}`
							: undefined
				}
			/>

			<BrandSections sections={sections} revealRef={revealRef} />
		</Frame>
	);
}

/* The page's own chrome — max width, padding, and the way back. Shared by the
   loading and loaded states so the page doesn't jump when data lands. */
function Frame({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			<button
				onClick={onBack}
				className="mb-5 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				Profile
			</button>
			{children}
		</div>
	);
}

/* The section-head edit control. Ghost + pencil, matching the "add one of
   these" buttons the file sections carry — editing prose is the same class of
   control, not a heavier one. */
function EditAction({ label, onClick }: { label: string; onClick: () => void }) {
	return (
		<Button variant="ghost" size="sm" onClick={onClick}>
			<Pencil size={14} data-icon="inline-start" />
			{label}
		</Button>
	);
}

function TileDelete({ onClick }: { onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			title="Delete"
			className="flex h-6 w-6 items-center justify-center rounded-[var(--r-2)] border border-line-2 bg-surface text-text-3 transition-colors hover:text-text-1"
		>
			<Trash2 size={11} />
		</button>
	);
}

/** A row in the story section's facts rail. Named so the list below can be
 * annotated up front — inferring it from the entries widens `value` per entry
 * and leaves the null filter with nothing consistent to narrow. */
interface BrandFact {
	label: string;
	value: React.ReactNode;
}

/* ────────────────────────── the brand, mapped ──────────────────────────
 * The package's mappers do the work; this assembles the few things that are
 * page-level — the stat strip, the facts rail, and the two preview lists.
 * Memoised on `brand` in the component, since every one of these allocates. */

function buildView(brand: NonNullable<ReturnType<typeof useGetBrandQuery>['data']>['getBrand']) {
	const logos = toLogoVariants(brand);
	const assets = toAssets(brand);
	const swatches = toSwatches(brand);
	const typefaces = toTypefaces(brand);
	const fontFiles = toFontFiles(brand);

	return {
		logos,
		assets,
		swatches,
		typefaces,
		fontFiles,
		colors: paletteHexes(brand),
		/* The logo grid's `brand` backdrop is one swatch, not a gradient. */
		primary: primaryHex(brand),
		stats: [
			{ label: 'Logo variants', value: logos.length, sectionId: 'logo' },
			{ label: 'Colors', value: swatches.length, sectionId: 'colors' },
			{ label: 'Typefaces', value: typefaces.length, sectionId: 'typography' },
			{ label: 'Files', value: assets.length, sectionId: 'assets' },
		],
		facts: (
			[
				brand.website
					? {
							label: 'Website',
							value: (
								<a
									href={brand.website}
									target="_blank"
									rel="noreferrer"
									className="text-text-1 underline decoration-line-2 underline-offset-2 hover:decoration-text-3"
								>
									{brand.website.replace(/^https?:\/\//, '')}
								</a>
							),
						}
					: null,
				brand.address?.formatted
					? { label: 'Location', value: brand.address.formatted }
					: null,
				brand.brandPersonality?.values && brand.brandPersonality.values.length > 0
					? { label: 'Values', value: brand.brandPersonality.values.join(', ') }
					: null,
				brand.createdAt ? { label: 'Created', value: formatDate(brand.createdAt) } : null,
			] as (BrandFact | null)[]
		).filter((fact): fact is BrandFact => fact !== null),
		/* Only files with a URL can be previewed; the rest still render as tiles
		   but aren't reachable in the viewer, so they must not shift its index. */
		logoPreviews: toPreviewItems(logos),
		assetPreviews: toPreviewItems(assets),
	};
}

function toPreviewItems(files: (BrandLogoVariant | BrandAsset)[]): FilePreviewItem[] {
	return files
		.filter((f) => f.url)
		.map((f) => ({
			id: f.id,
			url: f.url!,
			name: f.name,
			mimeType: f.mimeType,
			thumbnailUrl: f.thumbnailUrl,
		}));
}
